<template>
  <ul
    ref="gridRef"
    :class="[
      'grid-x grid-margin-x grid-margin-y sortable-grid-items',
      altGridViewEnabled ? altGridViewClasses : gridClasses,
      isSmallAltGridViewCard ? 'small-alt-grid-view' : '',
    ]"
  >
    <template v-if="loading">
      <li
        v-for="(item) in skeletonItems"
        :key="item"
        class="cell"
      >
        <Skeleton
          :height="skeletonHeight"
          width="100%"
        />
      </li>
    </template>
    <template v-else-if="noItems && priceRange">
      <div
        v-if="isBcom"
        class="body large-margin-top-l large-margin-bottom-s"
      >
        <p>We can't find any items for the filters you've selected.</p>
        <p>
          To see more results, please remove or change your filters.
        </p>
      </div>
      <div
        v-else
        class="horizontal-facet-group cell"
        :data-testid="idSortablegridNoItems"
      >
        <Card outlined="true">
          <p>
            We couldn't find any products within the specified price range: {{ priceRange }}
          </p>
          <p>Feel free to <b>reset your filters</b> for a broader search.</p>
        </Card>
      </div>
    </template>
    <template v-else-if="noItems">
      <div
        class="large-margin-top-l large-margin-bottom-s margin-left-xxs"
        :data-testid="idSortablegridNoItems"
      >
        <p
          v-if="!isBcom"
          class="large"
        >
          We couldn’t find a match for your search. Check the spelling or try a
          different search.
        </p>
        <div
          v-else
          class="large"
        >
          We couldn’t find a match for your search.
          <p class="bcom-zero-results medium large-margin-top-xxs small-margin-top-xxs large-margin-bottom-m">
            Check the spelling,
            <span v-if="isUSARegion">try a new search or</span>
            browse our list of
            <a
              class="link-heavy"
              href="/shop/all-designers?id=1001351&cm_sp=SEARCH-_-ZSR-_-Designer_Index-n"
            >designers</a>
            <span v-if="!isUSARegion"> or shop our
              <a
                class="link-heavy"
                href="/"
              >U.S. site</a>
            </span>.
          </p>
        </div>
      </div>
    </template>
    <template v-else>
      <template
        v-for="(item, index) in populatedProducts"
        :key="getKey(item, index)"
      >
        <ProductThumbnail
          v-if="item?.product"
          :product="item?.product"
          :index="index"
          :picture-props="pictureProps"
          :enable-lazy-for-eager-loaded="enableLazyForEagerLoaded"
          :device-type="{ isTablet, isDesktop, deviceType, isPhone }"
          :pzo-badging-enabled="pzoBadgingEnabled"
          :pzo-items="pzoItems"
          :is-simplification-on="isSimplificationOn"
          :simplification-with-v1="simplificationWithV1"
          :simplification-non-bold-percentage-exp="simplificationNonBoldPercentageExp"
          :simplification-remove-sale-exp="simplificationRemoveSaleExp"
          :selected-filters="selectedFilters"
          :loyalty-badge="loyaltyBadgeInfo"
          :is-alternative-images-view-enabled="isAlternativeImagesViewEnabled"
          :is-alternative-images-view-exp-3-enabled="isAlternativeImagesViewExp3Enabled"
          :color-swatch-enhancement="colorSwatchEnhancement"
          :disable-pzo-badges="disablePzoBadges"
          :macys-exclusive-badging="macysExclusiveBadging"
          :bcom-small-swatches="bcomSmallSwatches"
          :use-applied-mcom-copy-exp="useAppliedMcomCopyExp"
          :price-type-badge-exp="priceTypeBadgeExp"
          :mcom-font-weight="mcomFontWeight"
          :value-gift-badge-enabled="valueGiftBadgeEnabled"
          @getSponsoredItemRefs="sponsoredItemRefsData"
          @click="fireProdLinkTag(item?.product)"
          @chevronDirection="chevronDirection"
        />
        <ImageMap
          v-else-if="item?.imageMap || item?.banner"
          :image-map="item?.imageMap || item?.banner"
          :show-wrapper-url="!!item?.imageMap"
          :index="index"
        />
        <GoogleAdPlaceholder
          v-else-if="item?.googleAdOnIngridTopBanner || item?.googleAdOnIngridMidBanner || item?.googleAdOnIngridBottomBanner"
          :google-ad-on-ingrid-top-banner="item?.googleAdOnIngridTopBanner"
          :google-ad-on-ingrid-mid-banner="item?.googleAdOnIngridMidBanner"
          :google-ad-on-ingrid-bottom-banner="item?.googleAdOnIngridBottomBanner"
        />
        <BrandShowcasePlaceholder v-else-if="item?.brandShowcasePlaceholder" />

        <SortableGridProsCollection
          v-else-if="item?.prosCollection"
          :recommendation-config="item?.prosCollection?.recommendationConfig"
          :panel-config="item?.prosCollection?.panelConfig"
          :options="item?.prosCollection?.options"
        />
        <ConvoSearch
          v-else-if="item?.inGridFilters"
          :has-chevrons="!isPhone"
          :search-result="convoSearchResult"
          :experiment="currentConvoExperiment"
        />
      </template>
    </template>
  </ul>
</template>

<script setup>
/* istanbul ignore file */
import {
  ref, computed, watch, onMounted,
  defineAsyncComponent, provide,
} from 'vue';
import { useStore } from 'vuex';
import Skeleton from '@atomic-ui/skeleton';
import { fireTag } from '@vue-util/tagManager';
import useFireTagAsync from '@modules/discovery/composables/useFireTagAsync';
import useEnvParams from '@composables/useEnvParams';
import useDevice from '@modules/discovery/composables/useDevice';
import useKillSwitches from '@composables/useKillSwitches';
import useUSARegion from '@modules/discovery/composables/useUSARegion';
import splitPathByFilters from '@utils/splitPathByFilters';
import useThumbnailEligibility from '@modules/featureControl/composables/useThumbnailEligibility';
import useFeatureEligibility from '@modules/featureControl/composables/useFeatureEligibility';
import ConvoSearch from '@components/FilteringFacets/components/ConvoSearch/ConvoSearch';
import useMetaContext from '@modules/discovery/composables/useMetaContext';
import {
  useGridCssClasses, useBanners, useSkeletonItems,
} from '../composables';
import ProductThumbnail from './SortableGridProductThumbnail';
import { useProsCollection } from '../composables/useProsCollection';
import useSortableGridItems from '../composables/useSortableGridItems';
import useSortableGrid from '../composables/useSortableGrid';
import useLazyForEagerLoaded from '../composables/useLazyForEagerLoaded';
import formatPriceRange from '../utils/formatPriceRange';
import { idSortablegridNoItems } from '../../../../test/testIds';
import usePageType from '../../discovery/composables/usePageType';
import useLoyaltyBadgeInfo from '../composables/useLoyaltyBadgeInfo';
import useInGridFilters from '../composables/useInGridFilters';
import useConvoSearchExperiment from '../composables/useConvoSearchExperiment';
import useAltGridView from '../composables/useAltGridView';
import SortableGridProsCollection from './SortableGridProsCollection';
import BrandShowcasePlaceholder from './SortableGridBrandShowcasePlaceholder';
import GoogleAdPlaceholder from './GoogleAdPlaceholder';
import ImageMap from './SortableGridImageMap';

const Card = defineAsyncComponent(() => import('@common-vue/atomic-ui/src/components/card/Card'));

const props = defineProps({
  items: {
    type: Array,
    required: false,
    default: () => ([]),
  },
  productsList: {
    type: Array,
    required: false,
    default: () => ([]),
  },
  brand: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
    required: false,
  },
  loadingItems: {
    type: Number,
    default: () => 60,
  },
  noItems: {
    type: Boolean,
    required: false,
  },
  analytics: {
    type: Object,
    required: false,
  },
  altGridViewClasses: {
    type: String,
    required: false,
    default: '',
  },
});

const {
  isTablet, isDesktop, deviceType, isPhone,
} = useDevice();
const { pzoBadgingEnabled, secondRowBadgeEnabled, valueGiftBadgeEnabled } = useKillSwitches();
const { isBcom, enableAsyncAnalyticsTags } = useEnvParams();
const gridClasses = useGridCssClasses();
const gridRef = ref();
const itemsRef = computed(() => props.items);
const sponsoredItemRefs = {};
const { convoSearchResult, currentExperiment: currentConvoExperiment } = useConvoSearchExperiment();
const withBannersItems = useBanners(itemsRef);
const { populateProducts } = useProsCollection();
const _populatedProducts = populateProducts(withBannersItems);
const populatedProducts = useInGridFilters(_populatedProducts);
const { enableLazyForEagerLoaded } = useLazyForEagerLoaded(itemsRef);
const isUSARegion = useUSARegion();
const { pageTypeLiteral } = usePageType();

const {
  isAlternativeImagesViewEnabled, isAlternativeImagesViewExp3Enabled,
} = useFeatureEligibility(deviceType);

const {
  isSimplificationOn,
  simplificationWithV1,
  simplificationNonBoldPercentageExp,
  simplificationRemoveSaleExp,
  colorSwatchEnhancement,
  disablePzoBadges,
  macysExclusiveBadging,
  bcomSmallSwatches,
  useAppliedMcomCopyExp,
  priceTypeBadgeExp,
  mcomFontWeight,
} = useThumbnailEligibility();

const priceRange = ref('');
const updatePrice = () => {
  const facetNames = props.analytics?.facetNames;
  const facetValues = props.analytics?.facetValues;
  priceRange.value = formatPriceRange(facetValues, facetNames?.indexOf('PRICE'));
};

const HOME_CATEGORIES_SECOND_ROW = new Set([173142, 29391]);

const metaContext = useMetaContext();
const categoryId = Number(metaContext.value?.categoryId);
const isValidHomeSecondRowBadge = computed(() => secondRowBadgeEnabled && HOME_CATEGORIES_SECOND_ROW.has(categoryId));

// This is provided for product-thumbnail to consume
provide('isValidHomeSecondRowBadge', isValidHomeSecondRowBadge.value);

function firePZOLinkTag() {
  if (enableAsyncAnalyticsTags) {
    useFireTagAsync(() => fireTag('link', {
      event_name: 'pzo badge on plp',
      page_type: pageTypeLiteral,
      pzo_badge_exists: true,
    }));
  } else {
    fireTag('link', {
      event_name: 'pzo badge on plp',
      page_type: pageTypeLiteral,
      pzo_badge_exists: true,
    });
  }
}

const store = useStore();
const pzoItems = ref(null);
watch(() => store.getters.getPzoItems, (newPzoItems, oldPzoItems) => {
  /* istanbul ignore else */
  if (pzoBadgingEnabled && newPzoItems !== oldPzoItems) {
    /* istanbul ignore else */
    if (newPzoItems?.length) {
      firePZOLinkTag();
    }
    pzoItems.value = newPzoItems;
  }
});

// Watch for facets changes in 'analytics' prop
watch(() => props.analytics, updatePrice, { immediate: true });

const sponsoredItemRefsData = (sponsoredItemRef) => {
  if (sponsoredItemRef.value) {
    sponsoredItemRefs[sponsoredItemRef.key] = {
      elRef: sponsoredItemRef.value,
      analyticBeacons: sponsoredItemRef.analyticBeacons,
    };
  } else {
    delete sponsoredItemRefs[sponsoredItemRef.key];
  }
};

const pictureProps = computed(() => ({
  imageWidth: 342,
  retinaAdjustment: true,
  retinaQuality: '55,0',
}));

const loadingItemsRef = computed(() => props.loadingItems);
const { skeletonHeight, skeletonItems } = useSkeletonItems(gridRef, loadingItemsRef);

useSortableGridItems(itemsRef, sponsoredItemRefs);

function getKey(item, index) {
  return item.product?.id || item.imageMap?.id || item.banner || item.prosCollection
  || item.googleAdOnIngridTopBanner || item.googleAdOnIngridMidBanner || item.googleAdOnIngridBottomBanner
  || item.brandShowcasePlaceholder || item.inGridFilters || index;
}

function getIndexVal(item) {
  const prodIndex = props?.productsList?.findIndex((aItem) => aItem?.product?.id === item?.id);
  return prodIndex;
}
// Detect chevron click
const isChevronClick = ref('');
const chevronDirection = (direction) => {
  /* istanbul ignore else */
  if (direction === 'left' || direction === 'right') {
    isChevronClick.value = direction;
  }
};

function fireProdLinkTag(aItem) {
  // If chevron was clicked, do not fire the product link tag
  /* istanbul ignore else */
  if (isChevronClick.value) {
    isChevronClick.value = '';
    return;
  }
  const prodIndex = getIndexVal(aItem);
  const itemPos = (prodIndex > -1) ? (prodIndex + 1) : '';
  if (enableAsyncAnalyticsTags) {
    useFireTagAsync(() => fireTag('link', {
      event_name: 'product click',
      product_id: [aItem?.id?.toString()] || [''],
      product_position: [itemPos.toString()],
    }));
  } else {
    fireTag('link', {
      event_name: 'product click',
      product_id: [aItem?.id?.toString()] || [''],
      product_position: [itemPos.toString()],
    });
  }
  const url = new URL(window.location.href);
  const paramsObj = Object.fromEntries(url.searchParams.entries());
  /* istanbul ignore else */
  if (paramsObj.isZoneOfRelevance || paramsObj.prosSource || paramsObj.prosModelId || paramsObj.prosMilanId) {
    if (enableAsyncAnalyticsTags) {
      useFireTagAsync(() => fireTag('link', {
        event_name: 'recommendation link click',
        recommendation_model: paramsObj.prosModelId || '',
        recommendation_zone: paramsObj.prosZone || '',
        recommendation_milanid: paramsObj.prosMilanId || '',
        recommendation_position: itemPos.toString(),
      }));
    } else {
      fireTag('link', {
        event_name: 'recommendation link click',
        recommendation_model: paramsObj.prosModelId || '',
        recommendation_zone: paramsObj.prosZone || '',
        recommendation_milanid: paramsObj.prosMilanId || '',
        recommendation_position: itemPos.toString(),
      });
    }
  }
}

const { updatePZOBadging } = useSortableGrid();

const selectedFilters = computed(() => splitPathByFilters(store.getters.getSelectedFacetUrl));
const loyaltyBadgeInfo = useLoyaltyBadgeInfo();

const { altGridViewEnabled, isSmallAltGridViewCard } = useAltGridView();

onMounted(() => {
  if (!isBcom && pzoBadgingEnabled) {
    updatePZOBadging();
  }
});
</script>

<style lang="scss" scoped>
ul {
  li {
    list-style: none;
  }
}

@keyframes fadeIn1 {
  0% { opacity: 0; }
  20% { opacity: 0; }
  40% { opacity: 0; }
  60% { opacity: 0.6; }
  80% { opacity: 0.8; }
  100% { opacity: 1; }
}

@keyframes fadeIn2 {
  0% { opacity: 0; }
  20% { opacity: 0; }
  40% { opacity: 0; }
  60% { opacity: 0.6; }
  80% { opacity: 0.8; }
  100% { opacity: 1; }
}

@keyframes fadeIn3 {
  0% { opacity: 0; }
  20% { opacity: 0; }
  40% { opacity: 0; }
  60% { opacity: 0.6; }
  80% { opacity: 0.8; }
  100% { opacity: 1; }
}

.alt-grid-animate-1 {
  animation: fadeIn1 .5s ease;
}

.alt-grid-animate-2 {
  animation: fadeIn2 .5s ease;
}

.alt-grid-animate-portrait-1 {
  animation: fadeIn1 .8s ease;
}

.alt-grid-animate-portrait-2 {
  animation: fadeIn2 .8s ease;
}

.alt-grid-animate-portrait-3 {
  animation: fadeIn3 .8s ease;
}
</style>
<style lang="scss" brand="bcom" scoped>
.bcom-zero-results {
  font-weight: 300;
}
</style>
